import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import config from 'app/src/config';
import { sendError } from 'app/src/components/ErrorBoundary';
import { CURRENT_USER, LOGOUT } from 'app/src/components/Auth/queries';
import storage from 'app/src/utils/storage';

const IGNORE_ERRORS = ['USER_ERROR', 'NOT_FOUND'];

export const ClientContext = createContext();

const setupApolloClient = ({ uri, credentials, clearCredentials }) => {
  let headers = {};

  if (credentials) {
    headers = {
      ...credentials,
      'access-token': credentials.accessToken,
    };
  }

  const httpLink = new HttpLink({ uri, headers });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (networkError) {
      if (401 === networkError.statusCode) {
        clearCredentials();
      }
    } else if (graphQLErrors && graphQLErrors[0]) {
      if (!IGNORE_ERRORS.includes(graphQLErrors[0].extensions?.code)) {
        sendError({
          error: {
            name: 'GraphQL Error',
            message: graphQLErrors[0].message,
            stack: JSON.stringify(graphQLErrors),
          }
        });
      }
    }
  });

  const checkHeadersLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(data => {
      const context = operation.getContext();

      // TODO Native support
      if ('undefined' !== typeof(localStorage)) {
        if (context.response?.headers) {
          localStorage.setItem(
            'X-Request-Id',
            context.response.headers.get('X-Request-Id')
          );
        }
      }

      return data;
    });
  });

  return new ApolloClient({
    link: from([checkHeadersLink, errorLink, httpLink]),
    cache: new InMemoryCache(),
  });
};

export const ClientProvider = ({ children }) => {
  const [apolloClient, setApolloClient] = useState();
  const [credentials, setCredentials] = useState();
  const [currentUser, setCurrentUser] = useState();
  const [storageChecked, setStorageChecked] = useState(false);

  const clearCredentials = () => {
    updateCredentials(null);
  };

  const apolloClientForAuth = useMemo(() => (
    setupApolloClient({
      credentials,
      clearCredentials,
      uri: config.api.baseUri + '/auth',
    })
  ));

  useEffect(() => {
    if (credentials?.accessToken) {
      setApolloClient(setupApolloClient({
        credentials,
        clearCredentials,
        uri: config.api.uri,
      }));
    } else {
      setApolloClient(null);
    }
  }, [credentials]);

  const updateCredentials = value => {
    const data = { credentials: value };

    storage.save({ key: 'auth', data }).then(() => {
      if (credentials?.accessToken !== value?.accessToken) {
        setCredentials(value);
      }
    });

    if (!value) {
      setCurrentUser(null);
    }
  };

  useEffect(() => {
    storage.load({ key: 'auth' }).then(authData => {
      if (authData?.credentials) {
        setCredentials(authData.credentials);
      }

      setStorageChecked(true);
    });
  }, []);

  const refetchCurrentUser = useCallback(() => {
    if (apolloClient && credentials?.accessToken) {
      apolloClient.query({
        query: CURRENT_USER,
        fetchPolicy: 'no-cache',
      }).then(({ data }) => {
        setCurrentUser(data.currentUser);

        if (!data.currentUser) {
          setCredentials(null);
        }
      });
    }
  }, [apolloClient]);

  const logout = useCallback(() => {
    updateCredentials(null);
    apolloClientForAuth.mutate({ mutation: LOGOUT }).
      then(() => window.location.href = '/');
  });

  const isLoggedIn = !!credentials?.accessToken;

  if (!currentUser && isLoggedIn) {
    refetchCurrentUser();
  }

  const value = {
    apolloClient,
    apolloClientForAuth,
    updateCredentials,
    isLoggedIn,
    logout,
    currentUser,
    refetchCurrentUser,
    storageChecked,
    credentials,
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

export default ClientContext;
